import { Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const copy = {
  en: {
    title: 'Compare Plans',
    feature: 'Feature',
    free: 'Free',
    starter: 'Starter',
    connection: 'Connection',
    legacy: 'Legacy',
    features: [
      { name: 'Announcements Included', free: '3', starter: '+5', connection: '+10', legacy: '+25' },
      { name: 'AI Writing Assist', free: 'Basic', starter: 'Basic', connection: 'Full', legacy: 'Full' },
      { name: 'Remove Watermark', free: false, starter: true, connection: true, legacy: true },
      { name: 'Scheduling', free: false, starter: false, connection: true, legacy: true },
      { name: 'Print & PDF Export', free: false, starter: false, connection: true, legacy: true },
      { name: 'Magazine Flipbook View', free: false, starter: false, connection: true, legacy: true },
      { name: 'Video Messaging', free: false, starter: false, connection: false, legacy: true },
      { name: 'Talk-to-Text', free: false, starter: false, connection: false, legacy: true },
      { name: 'TRESLOG Reflection Space', free: false, starter: false, connection: false, legacy: true },
      { name: 'Recurring Annual Messages', free: false, starter: false, connection: false, legacy: true },
      { name: 'Keepsake Export', free: false, starter: false, connection: false, legacy: true },
    ],
  },
  es: {
    title: 'Comparar Planes',
    feature: 'Característica',
    free: 'Gratis',
    starter: 'Inicio',
    connection: 'Conexión',
    legacy: 'Legado',
    features: [
      { name: 'Anuncios Incluidos', free: '3', starter: '+5', connection: '+10', legacy: '+25' },
      { name: 'Escritura con IA', free: 'Básica', starter: 'Básica', connection: 'Completa', legacy: 'Completa' },
      { name: 'Eliminar Marca de Agua', free: false, starter: true, connection: true, legacy: true },
      { name: 'Programación', free: false, starter: false, connection: true, legacy: true },
      { name: 'Exportar PDF', free: false, starter: false, connection: true, legacy: true },
      { name: 'Vista Revista', free: false, starter: false, connection: true, legacy: true },
      { name: 'Mensajes en Video', free: false, starter: false, connection: false, legacy: true },
      { name: 'Voz a Texto', free: false, starter: false, connection: false, legacy: true },
      { name: 'Espacio de Reflexión TRESLOG', free: false, starter: false, connection: false, legacy: true },
      { name: 'Mensajes Anuales Recurrentes', free: false, starter: false, connection: false, legacy: true },
      { name: 'Exportar Recuerdos', free: false, starter: false, connection: false, legacy: true },
    ],
  },
  pt: {
    title: 'Comparar Planos',
    feature: 'Recurso',
    free: 'Grátis',
    starter: 'Inicial',
    connection: 'Conexão',
    legacy: 'Legado',
    features: [
      { name: 'Anúncios Incluídos', free: '3', starter: '+5', connection: '+10', legacy: '+25' },
      { name: 'Escrita com IA', free: 'Básica', starter: 'Básica', connection: 'Completa', legacy: 'Completa' },
      { name: 'Remover Marca d\'Água', free: false, starter: true, connection: true, legacy: true },
      { name: 'Agendamento', free: false, starter: false, connection: true, legacy: true },
      { name: 'Exportar PDF', free: false, starter: false, connection: true, legacy: true },
      { name: 'Visualização Revista', free: false, starter: false, connection: true, legacy: true },
      { name: 'Mensagens em Vídeo', free: false, starter: false, connection: false, legacy: true },
      { name: 'Voz para Texto', free: false, starter: false, connection: false, legacy: true },
      { name: 'Espaço de Reflexão TRESLOG', free: false, starter: false, connection: false, legacy: true },
      { name: 'Mensagens Anuais Recorrentes', free: false, starter: false, connection: false, legacy: true },
      { name: 'Exportar Lembranças', free: false, starter: false, connection: false, legacy: true },
    ],
  },
  fr: {
    title: 'Comparer les Plans',
    feature: 'Fonctionnalité',
    free: 'Gratuit',
    starter: 'Débutant',
    connection: 'Connexion',
    legacy: 'Héritage',
    features: [
      { name: 'Annonces Incluses', free: '3', starter: '+5', connection: '+10', legacy: '+25' },
      { name: 'Aide IA', free: 'Basique', starter: 'Basique', connection: 'Complète', legacy: 'Complète' },
      { name: 'Supprimer Filigrane', free: false, starter: true, connection: true, legacy: true },
      { name: 'Programmation', free: false, starter: false, connection: true, legacy: true },
      { name: 'Export PDF', free: false, starter: false, connection: true, legacy: true },
      { name: 'Vue Magazine', free: false, starter: false, connection: true, legacy: true },
      { name: 'Messages Vidéo', free: false, starter: false, connection: false, legacy: true },
      { name: 'Voix en Texte', free: false, starter: false, connection: false, legacy: true },
      { name: 'Espace de Réflexion TRESLOG', free: false, starter: false, connection: false, legacy: true },
      { name: 'Messages Annuels Récurrents', free: false, starter: false, connection: false, legacy: true },
      { name: 'Exporter Souvenirs', free: false, starter: false, connection: false, legacy: true },
    ],
  },
  ar: {
    title: 'مقارنة الخطط',
    feature: 'الميزة',
    free: 'مجاني',
    starter: 'البداية',
    connection: 'الاتصال',
    legacy: 'الإرث',
    features: [
      { name: 'الإعلانات المضمنة', free: '3', starter: '+5', connection: '+10', legacy: '+25' },
      { name: 'مساعدة الكتابة بالذكاء الاصطناعي', free: 'أساسي', starter: 'أساسي', connection: 'كامل', legacy: 'كامل' },
      { name: 'إزالة العلامة المائية', free: false, starter: true, connection: true, legacy: true },
      { name: 'الجدولة', free: false, starter: false, connection: true, legacy: true },
      { name: 'تصدير PDF', free: false, starter: false, connection: true, legacy: true },
      { name: 'عرض المجلة', free: false, starter: false, connection: true, legacy: true },
      { name: 'رسائل الفيديو', free: false, starter: false, connection: false, legacy: true },
      { name: 'تحويل الصوت لنص', free: false, starter: false, connection: false, legacy: true },
      { name: 'مساحة تأمل TRESLOG', free: false, starter: false, connection: false, legacy: true },
      { name: 'رسائل سنوية متكررة', free: false, starter: false, connection: false, legacy: true },
      { name: 'تصدير التذكارات', free: false, starter: false, connection: false, legacy: true },
    ],
  },
  ja: {
    title: 'プラン比較',
    feature: '機能',
    free: '無料',
    starter: 'スターター',
    connection: 'コネクション',
    legacy: 'レガシー',
    features: [
      { name: 'アナウンスメント数', free: '3', starter: '+5', connection: '+10', legacy: '+25' },
      { name: 'AI文章支援', free: '基本', starter: '基本', connection: 'フル', legacy: 'フル' },
      { name: 'ウォーターマーク削除', free: false, starter: true, connection: true, legacy: true },
      { name: 'スケジュール', free: false, starter: false, connection: true, legacy: true },
      { name: 'PDF出力', free: false, starter: false, connection: true, legacy: true },
      { name: 'マガジンビュー', free: false, starter: false, connection: true, legacy: true },
      { name: 'ビデオメッセージ', free: false, starter: false, connection: false, legacy: true },
      { name: '音声テキスト変換', free: false, starter: false, connection: false, legacy: true },
      { name: 'TRESLOG振り返りスペース', free: false, starter: false, connection: false, legacy: true },
      { name: '年次定期メッセージ', free: false, starter: false, connection: false, legacy: true },
      { name: '記念品出力', free: false, starter: false, connection: false, legacy: true },
    ],
  },
  ko: {
    title: '플랜 비교',
    feature: '기능',
    free: '무료',
    starter: '스타터',
    connection: '커넥션',
    legacy: '레거시',
    features: [
      { name: '포함된 공지', free: '3', starter: '+5', connection: '+10', legacy: '+25' },
      { name: 'AI 작문 도움', free: '기본', starter: '기본', connection: '전체', legacy: '전체' },
      { name: '워터마크 제거', free: false, starter: true, connection: true, legacy: true },
      { name: '예약', free: false, starter: false, connection: true, legacy: true },
      { name: 'PDF 내보내기', free: false, starter: false, connection: true, legacy: true },
      { name: '매거진 뷰', free: false, starter: false, connection: true, legacy: true },
      { name: '비디오 메시지', free: false, starter: false, connection: false, legacy: true },
      { name: '음성 텍스트 변환', free: false, starter: false, connection: false, legacy: true },
      { name: 'TRESLOG 성찰 공간', free: false, starter: false, connection: false, legacy: true },
      { name: '연간 반복 메시지', free: false, starter: false, connection: false, legacy: true },
      { name: '기념품 내보내기', free: false, starter: false, connection: false, legacy: true },
    ],
  },
  id: {
    title: 'Bandingkan Paket',
    feature: 'Fitur',
    free: 'Gratis',
    starter: 'Pemula',
    connection: 'Koneksi',
    legacy: 'Warisan',
    features: [
      { name: 'Pengumuman Termasuk', free: '3', starter: '+5', connection: '+10', legacy: '+25' },
      { name: 'Bantuan Penulisan AI', free: 'Dasar', starter: 'Dasar', connection: 'Lengkap', legacy: 'Lengkap' },
      { name: 'Hapus Watermark', free: false, starter: true, connection: true, legacy: true },
      { name: 'Penjadwalan', free: false, starter: false, connection: true, legacy: true },
      { name: 'Ekspor PDF', free: false, starter: false, connection: true, legacy: true },
      { name: 'Tampilan Majalah', free: false, starter: false, connection: true, legacy: true },
      { name: 'Pesan Video', free: false, starter: false, connection: false, legacy: true },
      { name: 'Suara ke Teks', free: false, starter: false, connection: false, legacy: true },
      { name: 'Ruang Refleksi TRESLOG', free: false, starter: false, connection: false, legacy: true },
      { name: 'Pesan Tahunan Berulang', free: false, starter: false, connection: false, legacy: true },
      { name: 'Ekspor Kenang-kenangan', free: false, starter: false, connection: false, legacy: true },
    ],
  },

  hi: {
    title: 'योजनाओं की तुलना करें',
    feature: 'सुविधा',
    free: 'Free',
    starter: 'Starter',
    connection: 'Connection',
    legacy: 'Legacy',
    features: [
      { name: 'Announcements Included', free: '3', starter: '+5', connection: '+10', legacy: '+25' },
      { name: 'AI Writing Assist', free: 'बुनियादी', starter: 'बुनियादी', connection: 'पूर्ण', legacy: 'पूर्ण' },
      { name: 'Remove Watermark', free: false, starter: true, connection: true, legacy: true },
      { name: 'Scheduling', free: false, starter: false, connection: true, legacy: true },
      { name: 'Print & PDF Export', free: false, starter: false, connection: true, legacy: true },
      { name: 'Magazine Flipbook View', free: false, starter: false, connection: true, legacy: true },
      { name: 'Video Messaging', free: false, starter: false, connection: false, legacy: true },
      { name: 'Talk-to-Text', free: false, starter: false, connection: false, legacy: true },
      { name: 'TRESLOG Reflection Space', free: false, starter: false, connection: false, legacy: true },
      { name: 'Recurring Annual Messages', free: false, starter: false, connection: false, legacy: true },
      { name: 'यादगार निर्यात', free: false, starter: false, connection: false, legacy: true },
    ],
  },
};

function CellValue({ value }: { value: boolean | string }) {
  if (typeof value === 'string') {
    return <span className="text-sm font-medium text-foreground">{value}</span>;
  }
  return value ? (
    <Check className="h-4 w-4 text-primary mx-auto" />
  ) : (
    <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
  );
}

export function PricingComparisonTable() {
  const { language } = useLanguage();
  const t = lc(copy, language);

  return (
    <motion.div
      className="max-w-4xl mx-auto mt-16"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <h3 className="text-xl font-bold text-foreground text-center mb-6">
        {t.title}
      </h3>
      <div className="rounded-xl border border-border overflow-hidden bg-background shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="w-[35%] text-foreground font-semibold">{t.feature}</TableHead>
              <TableHead className="text-center text-emerald-600 dark:text-emerald-400 font-semibold">{t.free}</TableHead>
              <TableHead className="text-center text-muted-foreground font-semibold">{t.starter}</TableHead>
              <TableHead className="text-center text-primary font-semibold">{t.connection}</TableHead>
              <TableHead className="text-center text-amber-600 dark:text-amber-400 font-semibold">{t.legacy}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {t.features.map((feature) => (
              <TableRow key={feature.name}>
                <TableCell className="text-sm text-muted-foreground font-medium">{feature.name}</TableCell>
                <TableCell className="text-center"><CellValue value={feature.free} /></TableCell>
                <TableCell className="text-center"><CellValue value={feature.starter} /></TableCell>
                <TableCell className="text-center"><CellValue value={feature.connection} /></TableCell>
                <TableCell className="text-center"><CellValue value={feature.legacy} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </motion.div>
  );
}
